import { FC } from 'react';
import { Popover } from '@headlessui/react';
import { DropDownChevronDownIcon, NavCloseIcon } from 'assets/icons';
import { twMerge } from 'tailwind-merge';

interface OptionItem {
  id: string;
  label: string;
}

interface MultiSelectDropdownProps {
  selectedValues: string[];
  options: OptionItem[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  buttonStyle?: string;
  disabled?: boolean;
  maxDisplayedValues?: number;
}

const MultiSelectDropdown: FC<MultiSelectDropdownProps> = ({
  selectedValues,
  options,
  onChange,
  placeholder,
  buttonStyle,
  disabled = false,
  maxDisplayedValues = 2
}) => {
  const handleChange = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const handleRemoveValue = (e: React.MouseEvent, valueToRemove: string) => {
    e.stopPropagation(); // Prevent dropdown from opening
    const newValues = selectedValues.filter(value => value.toLowerCase() !== valueToRemove.toLowerCase());
    onChange(newValues);
  };

  // Get label for a value
  const getLabelById = (id: string) => {
    const option = options.find(opt => opt.id === id);
    return option ? option.label : id;
  };

  const displaySelectedValues = () => {
    if (selectedValues.length === 0) return placeholder || 'Select values';
    return (
      <div className="flex flex-wrap items-center gap-2">
        {selectedValues.slice(0, maxDisplayedValues).map(value => (
          <span
            key={value}
            className="flex items-center gap-1 rounded bg-borderGray px-2 py-1 text-sm text-primaryText">
            {getLabelById(value)}
            <NavCloseIcon className="ml-1 h-3 w-3 cursor-pointer" onClick={e => handleRemoveValue(e, value)} />
          </span>
        ))}
        {selectedValues.length > maxDisplayedValues && (
          <span className="text-sbase text-primaryText">+ {selectedValues.length - maxDisplayedValues}</span>
        )}
      </div>
    );
  };

  return (
    <Popover className="relative">
      <Popover.Button
        disabled={disabled}
        className={twMerge(
          // eslint-disable-next-line max-len
          'relative flex min-h-[50px] w-full items-center justify-between rounded-md border border-borderGray bg-white px-3 py-3',
          disabled && 'cursor-not-allowed opacity-60',
          buttonStyle
        )}>
        <div className="flex-1 pr-6 text-left">{displaySelectedValues()}</div>
        <DropDownChevronDownIcon className="absolute right-3 top-[22px] h-[8px] w-[14px] shrink-0 text-gray-400" />
      </Popover.Button>

      {!disabled && (
        <Popover.Panel className="absolute z-10 mt-1 w-full rounded-md border border-borderGray bg-white p-2 shadow-lg">
          <div className="max-h-48 overflow-y-auto">
            {options.length > 0 ? (
              options.map(option => (
                <div key={option.id} className="mb-2 flex items-center px-2 py-1 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    id={option.id}
                    checked={selectedValues.includes(option.id)}
                    onChange={() => handleChange(option.id)}
                    className="h-4 w-4 rounded accent-secondary"
                  />
                  <label htmlFor={option.id} className="ml-3 cursor-pointer text-sbase text-primaryText">
                    {option.label}
                  </label>
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center p-2 text-sm text-gray-500">No options available</div>
            )}
          </div>
        </Popover.Panel>
      )}
    </Popover>
  );
};

export default MultiSelectDropdown;
